import {
    basicUpdatePermission,
    basicViewPermission,
    type PartialProfilePermissions,
    type ProfilePermissions,
    teamOwnerPermission,
    teamViewPermission,
} from "@qendo/permissions";
import type { DateArg } from "date-fns";
import type { QendoIconSet } from "~/components/QendoIcon";
import type { Profile } from "~/entities/profile/profile";
import type { DataPayload } from "~/util/data";

export enum ProfileRole {
    owner = "owner",
    support = "support",
    medical = "medical",
    share = "share",
    contact = "contact",
}

export const ProfileRoles = Object.values(ProfileRole);

export const ProfileRoleMap: Record<ProfileRole, string> = {
    [ProfileRole.owner]: "Owner",
    [ProfileRole.support]: "Support",
    [ProfileRole.medical]: "Medical",
    [ProfileRole.share]: "Share",
    [ProfileRole.contact]: "Contact",
};

export const ProfileRoleIconMap: Record<ProfileRole, QendoIconSet> = {
    [ProfileRole.owner]: "profile",
    [ProfileRole.support]: "family-time",
    [ProfileRole.medical]: "doctor",
    [ProfileRole.share]: "share",
    [ProfileRole.contact]: "contact",
};

export type TeamProfileRole = ProfileRole.owner | ProfileRole.medical | ProfileRole.support;
export type UserBackedProfileRole = TeamProfileRole | ProfileRole.share;
export type ContactableProfileRole = TeamProfileRole | ProfileRole.contact;
export type SupportProfileRole = ProfileRole.medical | ProfileRole.support;

export const TeamProfileRoles: TeamProfileRole[] = [ProfileRole.owner, ProfileRole.medical, ProfileRole.support];
export const SupportProfileRoles: SupportProfileRole[] = [ProfileRole.medical, ProfileRole.support];
export const ContactableProfileRoles: ContactableProfileRole[] = [...TeamProfileRoles, ProfileRole.contact];

export const SelectableProfileRoleMap: Record<TeamProfileRole, string> = {
    [ProfileRole.owner]: ProfileRoleMap[ProfileRole.owner],
    [ProfileRole.support]: ProfileRoleMap[ProfileRole.support],
    [ProfileRole.medical]: ProfileRoleMap[ProfileRole.medical],
};

export type ShareRolePayload = {
    role: ProfileRole.share;
    permissions: PartialProfilePermissions;
};

export type OwnerRolePayload = {
    role: ProfileRole.owner;
    permissions?: null;
};

export type TeamRolePayload<T extends TeamProfileRole> = {
    role: T;
    permissions?: PartialProfilePermissions | null;
};

export type PermissionPayload<T extends ProfileRole> = T extends ProfileRole.owner
    ? OwnerRolePayload
    : T extends ProfileRole.share
      ? ShareRolePayload
      : T extends TeamProfileRole
        ? TeamRolePayload<T>
        : never;

export type ProfileData = {
    id: string;
    name: string;
    avatar: string;
} & DataPayload<Profile>;

export interface ProfileTeamData {
    id: string;
    profile_id: string;
    team_role: ProfileRole;
    name: string;
    avatar: string | null;
    emergency_contact: boolean;
}

export interface EmergencyInstructionData {
    id: string;
    description: string;
    photograph_ids: string[];
}

export interface PeriodData {
    start_at: DateArg<Date> | string;
    started_at?: DateArg<Date> | string | null;
    end_at: DateArg<Date> | string;
    ended_at?: DateArg<Date> | string | null;
}

export const CycleOptions = [
    { value: -1, label: "No cycle - Mirena" },
    { value: 0, label: "No cycle" },
    { value: 1, label: "Constant bleeding" },
    ...[...Array.from({ length: 90 }).keys()].map((k) => {
        const value = k + 2;

        return { value, label: `${value} days` };
    }),
];

export interface CycleData {
    cycle: number;
    period: number;
    tracked: boolean;
    estimate: boolean;
}

export interface HasAvatar {
    avatar?: string | null;
}

export const DefaultPermissionsMap: Record<ProfileRole, Partial<ProfilePermissions>> = {
    [ProfileRole.owner]: {
        profile: basicUpdatePermission,
        goals: basicUpdatePermission,
        journal: basicUpdatePermission,
        activities: basicUpdatePermission,
        symptoms: basicUpdatePermission,
        cycle: basicUpdatePermission,
        calendar: basicUpdatePermission,
        documents: basicUpdatePermission,
        medical: basicUpdatePermission,
        team: teamOwnerPermission,
        photos: basicUpdatePermission,
    },
    [ProfileRole.support]: {
        profile: basicViewPermission,
        goals: basicViewPermission,
        journal: basicViewPermission,
        activities: basicViewPermission,
        symptoms: basicViewPermission,
        cycle: basicViewPermission,
        calendar: basicViewPermission,
        documents: basicViewPermission,
        medical: basicViewPermission,
        team: teamViewPermission,
        photos: basicViewPermission,
    },
    [ProfileRole.medical]: {
        symptoms: basicViewPermission,
        cycle: basicViewPermission,
        medical: basicViewPermission,
        photos: basicViewPermission,
    },
    [ProfileRole.share]: {},
    [ProfileRole.contact]: {},
};
